<template lang="pug">
.domain
    .flex-row
        .col.grow
            el-form-item(:label="$t('Domain / IP')" :prop="`domains.${index}.base_url`")
                el-input(v-model="url" maxlength="255" show-word-limit)
        .col.guest-cg(v-if="alternativeGuestCustomerGroupsFeatureEnabled")
            el-form-item(:label="$t('Gast-Kundengruppe')")
                el-select(
                    v-model="guestCustomerGroupId"
                    clearable
                    :disabled="!guestEligibleGroupOptions.length"
                    :placeholder="guestEligibleGroupOptions.length ? $t('Shop-Standard') : $t('No guest-eligible groups yet')"
                    :no-data-text="$t('No guest-eligible groups yet')"
                )
                    el-option(
                        v-for="option in guestEligibleGroupOptions"
                        :key="option.id"
                        :label="option.currency ? `${option.name} (${option.currency.iso4217})` : option.name"
                        :value="option.id"
                    )
        template(v-if="isUnderConstructionFeatureEnabled")
            el-switch(
                :active-text="$t('Under Construction')"
                v-model="underConstruction"
            )
        el-button.delete(
            v-if="deletable"
            circle type="danger" size="mini"
            icon="el-icon-fa-trash-alt far"
            @click="deleteDomain"
        )
    transition(name="slide-decent-vertical")
        .row(v-if="isUnderConstructionFeatureEnabled && underConstruction")
            el-form-item(:label="$t('Under Construction Message')" :prop="`domains.${index}.under_construction_message`")
                quill-editor(v-model="underConstructionMessage" :options="quillConfig")
</template>

<i18n>
{
    "de": {
        "Under Construction": "Wartungsmodus",
        "Allow Admin Access": "Admin-Zugriff erlauben",
        "Under Construction Message": "Wartungsmodus Nachricht",
        "Gast-Kundengruppe": "Gast-Kundengruppe",
        "Shop-Standard": "Shop-Standard",
        "No guest-eligible groups yet": "Noch keine Gast-fähigen Gruppen"
    },
    "en": {
        "Gast-Kundengruppe": "Guest customer group",
        "Shop-Standard": "Shop default",
        "No guest-eligible groups yet": "No guest-eligible groups yet"
    }
}
</i18n>

<script>

import _ from 'lodash'
import { getUnixTime } from 'date-fns';

import quillConfig from '@/quill/config';
import { useSettingsStore, useSetupStore } from '@/stores'

export default {
    name: 'Domain',
    props: {
        data: {
            type: Object,
            required: true,
        },
        index: {
            type: Number,
            required: true,
        },
        deletable: {
            type: Boolean,
            default: false,
        },
        // Provided by AbonementBasic — single fetch shared by all Domain rows.
        // Pre-filtered to guest_eligible = true.
        guestEligibleGroups: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            // mainPriceType: 'main',
            quillConfig,
            quillPostLinksFeatureEnabled: false,
        };
    },
    computed: {
        url: {
            get() {
                return _.get(this.data, 'base_url', '')
            },
            set(value) {
                useSettingsStore().setFormDomain({ index: this.index, value })
            }
        },
        underConstruction: {
            get() {
                return !!_.get(this.data, 'under_construction_since', false)
            },
            set(value) {
                if (value) value = getUnixTime(new Date())
                else value = null
                useSettingsStore().setFormDomainUnderConstructionSince({ index: this.index, value })

                if (value) this.allowAdminAccess = true
                else this.allowAdminAccess = false
            }
        },
        allowAdminAccess: {
            get() {
                return _.get(this.data, 'allow_admin_access', false)
            },
            set(value) {
                useSettingsStore().setFormDomainAllowAdminAccess({ index: this.index, value })
            }
        },
        underConstructionMessage: {
            get() {
                return _.get(this.data, 'under_construction_message', '')
            },
            set(value) {
                useSettingsStore().setFormDomainUnderConstructionMessage({ index: this.index, value })
            }
        },
        isUnderConstructionFeatureEnabled() {
            let features = _.get(this.activeCms, 'config.template.features') || [];
            return !!features.find(el => el === 'under-construction');
        },
        alternativeGuestCustomerGroupsFeatureEnabled() {
            let features = _.get(this.activeCms, 'config.template.features') || [];
            if (! features.includes('alternative-guest-customer-groups')) return false;

            // Feature is opt-in per CMS via the picker-mode setting. `none`
            // (default) means the admin hasn't activated guest market handling
            // for this shop, so we hide the whole UI surface.
            const pickerMode = _.get(useSetupStore(), 'settingValues["guest-market-picker-mode"]');
            return pickerMode && pickerMode !== 'none';
        },
        guestCustomerGroupId: {
            get() {
                return _.get(this.data, 'guestCustomerGroup.id') || null;
            },
            set(value) {
                useSettingsStore().setFormDomainGuestCustomerGroup({
                    index: this.index,
                    value: value ? { id: value } : null,
                });
            },
        },
        guestEligibleGroupOptions() {
            // Parent already filters on guest_eligible — just normalise null.
            return this.guestEligibleGroups || [];
        },
        // toggleUnderConstruction() {
        //     store.commit(`settings/form/toggleUnderConstruction`, { index: this.index })
        // },
        options() {
            return useSettingsStore().options;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
    },
    methods: {
        deleteDomain() {
            useSettingsStore().deleteFormDomain({ index: this.index })
        },
    },
}
</script>

<style lang="sass" scoped>
#body
    .el-main.main-slot .domains
        & > .domain
            .flex-row
                align-items: center
                gap: 12px
            .col.guest-cg
                flex: 0 0 220px
                ::v-deep .el-select
                    width: 100%
            &:not(:first-child)
                .col::v-deep label.el-form-item__label
                    display: none
                .el-switch,
                button.delete
                    margin-bottom: 24px
</style>
